import { useCallback, useRef, useState } from "react";

export interface UseStickyObserverResult<T extends HTMLElement> {
  /**
   * Attach to the element that has `position: sticky`.
   */
  ref: (node: T | null) => void;
  /**
   * `true` while the element is stuck to its `top` offset.
   */
  isStuck: boolean;
}

/**
 * Tracks whether a `position: sticky` element is currently pinned at its top offset.
 *
 * @param top CSS value used as the sticky `top` offset (same shape as MUI sx `top`,
 *   e.g. `56`, `"56px"`, `"var(--sinch-header-height, 56px)"`, `"calc(1rem + 8px)"`).
 *   The hook resolves it to pixels via a hidden probe element to derive the
 *   `IntersectionObserver` rootMargin.
 */
export const useStickyObserver = <T extends HTMLElement = HTMLDivElement>(
  top: number | string
): UseStickyObserverResult<T> => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isStuck, setIsStuck] = useState(false);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;
      const offsetPx = resolveTopPx(top);
      const observer = new IntersectionObserver(([entry]) => setIsStuck(entry.intersectionRatio < 1), {
        rootMargin: `-${offsetPx + 1}px 0px 0px 0px`,
        threshold: [1],
      });
      observer.observe(node);
      observerRef.current = observer;
    },
    [top]
  );

  return { ref, isStuck };
};

const resolveTopPx = (top: number | string): number => {
  if (typeof top === "number") return top;
  const probe = document.createElement("div");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.top = top;
  document.body.appendChild(probe);
  const px = parseFloat(getComputedStyle(probe).top);
  document.body.removeChild(probe);
  return Number.isFinite(px) ? px : 0;
};
